import { CALM_SCENES } from "../../../lib/calmScenes";
import { blueCommotion } from "./blueCommotion";
import { broodingMind } from "./broodingMind";
import { existence } from "./existence";
import { homeOnTheWay } from "./homeOnTheWay";
import { marshyMeadows } from "./marshyMeadows";
import { medley } from "./medley";
import { stormWhispers } from "./stormWhispers";
import { pondLilies } from "./pond";
import { treeWindow } from "./treeWindow";
import { oakWinter } from "./oakWinter";
import { sunsetPlay } from "./sunsetPlay";
import { tulips } from "./tulips";
import { orchard } from "./orchard";
import { garden } from "./garden";
import { butterfly } from "./butterfly";
import { vasundhara } from "./vasundhara";
import { home } from "./home";
import { shiva } from "./shiva";
import { faith } from "./faith";
import { ganges } from "./ganges";
import { soulWindow } from "./soulWindow";
import { morPankh } from "./morPankh";
import { love } from "./love";
import { destiny } from "./destiny";
import { bouquet } from "./bouquet";
import { dream } from "./dream";
import { promise } from "./promise";
import { summerSunset } from "./summerSunset";
import { simmering } from "./simmering";

// Story arcs: a painting told as stages, her line, touch, words and ambient sound; played after its projection room.
const ARCS = [blueCommotion, stormWhispers, broodingMind, marshyMeadows, medley, homeOnTheWay, existence, pondLilies, treeWindow, oakWinter, sunsetPlay, tulips, orchard, garden, butterfly, vasundhara, home, shiva, faith, ganges, soulWindow, morPankh, love, destiny, bouquet, dream, promise, summerSunset, simmering];

// Every journey ends as her real painting emerges stroke by stroke, the strokes growing out of its own climax.
const REVEALS = {
  "a-blue-commotion": { style: "circular", from: "center", lead: { type: "swirl", at: [0.5, 0.5] } },
  "the-storm-of-whispering-thoughts": { style: "circular", from: "center" },
  "the-brooding-mind": { style: "flow", from: "left" },
  "marshy-meadows-of-memories": { style: "flow", from: "bottom" },
  medley: { style: "flow", from: "top", lead: { type: "pour", at: [0.5, 0.05] } },
  "that-home-on-the-way": { style: "vertical", from: "top" },
  existence: { style: "circular", from: "center", lead: { type: "swirl", at: [0.5, 0.5] } },
  "the-beauty-of-faith": { style: "flow", from: "top" },
  "the-lotus-pond": { style: "radial", from: "center" },
  "the-tree-by-the-window": { style: "flow", from: "center" },
  "oak-city-winter": { style: "vertical", from: "top" },
  "sunset-play": { style: "horizontal", from: "center" },
  tulips: { style: "vertical", from: "bottom" },
  "the-orchard": { style: "flow", from: "bottom" },
  "flower-garden": { style: "flow", from: "bottom" },
  "the-butterfly-wing": { style: "radial", from: "center" },
  "vasundhara-the-planet": { style: "circular", from: "center", lead: { type: "swirl", at: [0.5, 0.5] } },
  home: { style: "radial", from: "center" },
  "hridayaye-shivastishthatu": { style: "flow", from: "top", lead: { type: "pour", at: [0.5, 0.0] } },
  "the-window-of-our-soul": { style: "flow", from: "center" },
  "mor-pankh": { style: "radial", from: "center" },
  "an-evening-by-the-ganges": { style: "horizontal", from: [0.5, 0.65], at: 25.2, lead: { type: "curl", at: [0.5, 0.68] } },
};
ARCS.forEach((a) => (a.reveal = { ...REVEALS[a.slug], ...a.reveal }));
export const REVEAL_DUR = 10;
export const revealAt = (arc) => arc.reveal?.at ?? arc.duration - 4;
export const revealEnd = (arc) => revealAt(arc) + (arc.reveal?.duration ?? REVEAL_DUR);
export const arcFor = (painting) => (painting && ARCS.find((a) => a.slug === painting.slug)) || null;

export const stageAt = (arc, t) => arc.stages.reduce((s, x) => (t >= x.at ? x : s), arc.stages[0]);

export function keyAt(k, t) {
  if (t <= k[0][0]) return k[0][1];
  for (let i = 1; i < k.length; i++) {
    const [t0, v0] = k[i - 1];
    const [t1, v1] = k[i];
    if (t <= t1) return v0 + ((v1 - v0) * (t - t0)) / (t1 - t0);
  }
  return k[k.length - 1][1];
}

export function speedAt(arc, t) {
  return keyAt(arc.speed, t);
}

// Her "About the work", told a phrase at a time: sentences, long ones split at a comma or colon.
function phrases(text) {
  const out = [];
  for (const s of (text || "").split(/(?<=[.!?])\s+/).map((x) => x.trim()).filter(Boolean)) {
    if (s.length <= 95) {
      out.push(s);
      continue;
    }
    let cur = "";
    for (const part of s.split(/(?<=[,:;])\s+/)) {
      if (cur && (cur + " " + part).length > 95) {
        out.push(cur);
        cur = part;
      } else cur = cur ? `${cur} ${part}` : part;
    }
    if (cur) out.push(cur);
  }
  return out;
}

// Captions paced by reading length; they pause while her highlighted line arrives.
export function captionPlan(arc, about, calm) {
  const pauseAt = calm ? arc.calm.line[0] : arc.line.from;
  const pause = [pauseAt - 0.4, pauseAt + 6.4];
  let t = calm ? 1 : 1.5;
  return [...phrases(about), ...[].concat(arc.captionTail ?? [])].map((text) => {
    const d = Math.min(5.5, Math.max(2.4, text.length / 15));
    if (t + d > pause[0] && t < pause[1]) t = pause[1];
    const c = { text, from: t, to: t + d };
    t += d + 0.35;
    return c;
  });
}

// When an arc earns its "See the painting ✦": its own length, or later if the story needs more time.
export function arcEnd(arc, about, calm) {
  if (calm) return calmPlan(arc, about).end;
  const plan = captionPlan(arc, about, calm);
  return Math.max(revealEnd(arc) + 0.6, (plan.length ? plan[plan.length - 1].to : 0) + 0.8);
}

// The calm telling still plays every stage in order (a painted still of each, slowly drifting), then cross-fades into her painting.
export function calmPlan(arc, about) {
  const plan = captionPlan(arc, about, true);
  const capEnd = plan.length ? plan[plan.length - 1].to : 0;
  // each stage shows the painted panorama that is on at that moment in the full journey (or a captured still)
  const wraps = (arc.layers || []).filter((l) => l.wrap);
  const wrapAt = (t) => [...wraps].reverse().find((l) => (l.show?.[1] ?? 0) <= t + 2) || wraps[0];
  const frames = CALM_SCENES[arc.slug] || (wraps.length ? arc.stages.filter((s) => s.at < revealAt(arc)).map((s) => ({ hint: s.hint, src: wrapAt(s.at + 1).src })) : null);
  const scenes = frames || arc.stages.filter((s) => s.at < revealAt(arc)).map((s) => ({ hint: s.hint }));
  const n = Math.max(1, scenes.length);
  const per = Math.min(8, Math.max(4.5, (capEnd - 6) / n));
  const f0 = n * per;
  const calmHint = (h) => (/^(tap|hold|press|drag|swipe|sweep|touch|move|blow|tilt)\b/i.test(h || "") ? `${h} (or just watch)` : h);
  return { scenes, frames: !!frames, per, fade: [f0, f0 + 4.5], line: [f0 + 2, f0 + 999], end: Math.max(f0 + 7, capEnd + 0.8), hintAt: (t) => (t >= f0 ? "breathe in" : calmHint(scenes[Math.min(n - 1, Math.floor(t / per))]?.hint)) };
}
